//Identify.ts =================================================
import "../Util/Prototypes";

import * as GetSearchResults from "../Search/GetSearchResults";
import * as GraLay from "../Util/GraLay";
import * as Identify from "../Widgets/Identify";
import * as IdentifyPopup from "../Widgets/IdentifyPopup";
import * as MainMap from "../MainMap";
import * as ResultsFS from "../Util/ResultsFS";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

export let results_fs: ResultsFS.iResult[] = [];
export let grandTotRec: number = 0;

//Module variables.
let ii: number; //Index into the identifiable layers array.
let query: __esri.Query;
let geom: __esri.Point; // Point geometry to identify on.
let layer: Search.iLayer; // Layer object.
let identifiableLayers: GetSearchResults.iSelectableLayers[]; // List of all layers currently being identified.
let layers: Search.iLayer[] = []; // Array of all layers containing identified features.

//Initialize identify for feature layers.
export function init(pnt: __esri.Point) {
   Util.logMsg("Top of Identify.init");
   if (Spinner.busy) {
      return; //Quit if busy.
   }
   Spinner.show(true);

   //Init.
   ii = -1;
   geom = pnt;
   layers = [];
   grandTotRec = 0;
   identifiableLayers = Util_App.getVisibleSelectOnMapLayerNames("Identify");

   if (!Settings.identifyEnabled) {
      identifiableLayers = []; //Disable identify tool.
   }

   identifyFeatures();
}

//Identify features for feature layers.
function identifyFeatures(): void {
   Util.logMsg("Top of Identify.identifyFeatures");
   ii++; //Process next layer.

   if (ii < identifiableLayers.length) {
      const layerName: string = identifiableLayers[ii].name;
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);

      layer = {
         name: layerName, spec: laySpec, fs: null, IDs: [], prevQuery: null, begRec: 0, endRec: 0,
         numRec: Settings.maxSearchRecPerPage, totRec: 0, zoomAllMBR: null,
      };

      executeQueryTask();

   } else { //All map services have been processed.
      displayResults(); //Build graphic results.
   }
}

//Perform spatial query.
function executeQueryTask() {
   Util.logMsg("Top of Identify.executeQueryTask");

   //Build query.
   query = new Query({
      returnGeometry: true,
      outSpatialReference: MainMap.activeView.spatialReference,
      geometry: geom,
      maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
      outFields: ["*"],
   });

   if (!MainMap.activeView.spatialReference.isWebMercator) {
      query.geometryPrecision = 0;
   }

   //Add a buffer when identifying points and lines with point geometry.
   if (geom.type === "point" && layer.spec.geometryType !== "polygon") {
      query.distance = Util_App.getSelectByPointTolerance(true);

      query.units = "feet"; //Units is required for some bug reason.
      //if (MainMap.activeView.spatialReference.isWebMercator) {
      //   query.units = "meters";
      //}
   }

   if (layer.spec.sortBy !== "") {
      query.orderByFields = [layer.spec.sortBy];
   }

   const url: string = Util_App.getLayerURL(layer.spec);
   QueryTask.executeQueryJSON(url, query).then(processQueryResults).catch(queryTask_failed);
}

//Process query results.
function processQueryResults(fs: __esri.FeatureSet) {
   Util.logMsg("Top of Identify.processQueryResults");

   layer.fs = fs;
   layer.totRec = fs.features.length;
   layer.endRec = Math.min(layer.totRec, Settings.maxRecordCount) - 1;
   layer.numRec = Math.min(layer.totRec, Settings.maxSearchRecPerPage);

   //Save results.
   if (fs.features.length > 0) {

      //Sort results.
      if (layer.spec.sortBy !== "") {
         fs.features.sort(Util_App.sortFC(layer.spec.sortBy));
      }

      //Add new layers to the master identify array results.
      layers.Add(layer);
   }

   //Process next identifiable layer.
   identifyFeatures();
}

//Display results.
function displayResults(): void {
   Util.logMsg("Top of Identify.displayResults");
   //Util.errMsg("Identify Results", layers);
   Spinner.hide(); //Hide the spinner for identify.
   GraLay.clearHoverGraphics();

   //Compute grand total.
   for (const lay of layers) {
      grandTotRec += lay.totRec;
   }

   //No records found.
   if (Identify.grandTotRec === 0) {
      Spinner.hide();
      //SnackBar.show("No results found at this location", 2);
      return;
   }

   //Highlight features on map.
   GraLay.clearIdentifyHighlightGraphics();

   for (const lay of layers) {
      const fs = lay.fs;

      //Set zoom to layer map extent.
      lay.zoomAllMBR = Util_App.getMBRFeatureSet(fs);
      const sym: any = Util_App.getHighlightSymbol(fs, "Identify");

      for (let i = lay.begRec; i <= lay.endRec; i++) {
         const gra: any = fs.features[i];

         if (gra.geometry) {
            gra.symbol = sym; //Set symbology of identify feature.

            if (fs.geometryType === "point") {
               GraLay.identifyPoint.add(gra);

            } else if (fs.geometryType === "polyline") {
               GraLay.identifyLine.add(gra);

            } else { //polygon.
               GraLay.identifyPoly.add(gra);
            }
         }
      }
   }

   //Build results array by point, line, polygon order.
   let result_rec: ResultsFS.iResult;
   results_fs = [];

   for (const lay of layers) {
      if (lay.fs.geometryType === "point") {
         result_rec = {
            toolName: "Identify Results",
            layerName: lay.name,
            fs: lay.fs, IDs: [], begRec: 0, numRec: lay.fs.features.length,
            layerNameVisible: true, append: false,
         };
         Identify.results_fs.Add(result_rec);
      }
   }

   for (const lay of layers) {
      if (lay.fs.geometryType === "polyline") {
         result_rec = {
            toolName: "Identify Results",
            layerName: lay.name,
            fs: lay.fs, IDs: [], begRec: 0, numRec: lay.fs.features.length,
            layerNameVisible: true, append: false,
         };
         Identify.results_fs.Add(result_rec);
      }
   }

   for (const lay of layers) {
      if (lay.fs.geometryType === "polygon") {
         result_rec = {
            toolName: "Identify Results",
            layerName: lay.name,
            fs: lay.fs, IDs: [], begRec: 0, numRec: lay.fs.features.length,
            layerNameVisible: true, append: false,
         };
         Identify.results_fs.Add(result_rec);
      }
   }

   //Show identify results.
   IdentifyPopup.show();
}

//Query task failed.
function queryTask_failed(err): void {
   let msg = "QueryTask failed. URL of table: ";
   if (query.where) {
      msg += "Where: " + query.where + " ";
   }
   if (query.outFields) {
      msg += "Output fields: " + query.outFields.join(",") + " ";
   }
   errMsg(msg, err);
}

//Show error message.
function errMsg(msg: string, error?): void {
   if (error) {
      Util.errMsg(msg, error);
   } else {
      Util.errMsg(msg);
   }
   Spinner.hide();
}
